import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App.js';
import { AuthProvider } from './auth/AuthProvider.js';
import {
  SiteBrandingProvider,
  applyRememberedSiteIcon,
  applyRememberedTheme,
} from './site/SiteBranding.js';
import { applyRememberedColorScheme } from './site/ColorScheme.js';
import { applyRememberedLanguage } from './i18n/language.js';
import { followSignOutReturn } from './auth/signOutReturn.js';
import './styles.css';

const container = document.getElementById('root');
if (!container) throw new Error('The #root element is missing from index.html.');

// The saved theme, from the last visit, before anything is painted - otherwise a
// site on Amber flashes Classic until the bootstrap arrives. See SiteBranding.tsx.
applyRememberedTheme();

// The last known site icon into the browser tab, for the same reason: the row
// arrives after the first paint, and without this the tab shows nothing and
// then the icon on every load. Also SiteBranding.tsx.
applyRememberedSiteIcon();

// And the reader's light/dark choice, for the same reason: a reader who pinned
// dark would otherwise get a white page until React mounts the footer. Nothing
// stored means nothing applied, and the browser's own setting decides. See
// ColorScheme.ts.
applyRememberedColorScheme();

// And the reader's language onto <html lang> (S48). The words themselves need no
// early start - React renders them in the remembered language from the first
// paint - but the document's language is read by the browser and by screen
// readers before that. See i18n/language.ts.
applyRememberedLanguage();

/*
 * Back from signing out: Entra returns to /admin, the one address it may, and the
 * page the reader signed out from was noted before they left. Going straight on to
 * it, before anything renders, keeps the admin gate from flashing past on the way.
 * See auth/signOutReturn.ts.
 */
if (!followSignOutReturn()) render(container);

/*
 * AuthProvider sits outside the router because it consumes the redirect fragment
 * MSAL leaves in the URL, and that has to happen before any route is matched -
 * otherwise React Router sees a URL with an authentication response in it and
 * either renders the wrong route or strips the response before MSAL reads it.
 */
function render(root: HTMLElement): void {
  createRoot(root).render(
    <StrictMode>
      <AuthProvider>
        {/* Outside the router too: both shells read the brand, and the Settings
            page writes it, so it has to outlive any one route. */}
        <SiteBrandingProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </SiteBrandingProvider>
      </AuthProvider>
    </StrictMode>,
  );
}
